import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';

import { AuthService, YeehroCalendlyAvailabilityService, YeehroCalendlyBookingService, YeehroCalendlyUserSettingsService, YeehroCalendlyWorkflowService } from '@app/services';

// Reassign an existing booking's host. Dialog captures the admin's choice of
// a new host, an optional new time slot (uses the shared booking-calendar for
// slot picking), and an optional note. Resolves with the admin's selection
// (`{ newHost, newStart?, newEnd?, note }`). Caller (meetings component)
// runs the reassign endpoint + notification.
//
// Inputs (set by caller via dialogRef.componentInstance):
//   booking - the YeehroCalendlyBooking being reassigned. Required.
@Component({
  selector: 'booking-reassign-dialog',
  templateUrl: './booking-reassign-dialog.component.html',
  styleUrls: ['./booking-reassign-dialog.component.scss'],
  standalone: false
})
export class BookingReassignDialogComponent implements OnInit {
  booking: any = null;

  // Recruiter dropdown data. Pre-loaded from the availability service so we
  // filter down to hosts who actually have a schedule set up (a recruiter
  // with no availability record would render an empty calendar and can't
  // meaningfully be reassigned to). Passed via [passedUsers] on user-filter-select.
  availableHosts: any[] = [];
  hostsLoaded: boolean = false;

  selectedHost: any = null;
  note: string = '';

  // Time change picker. Radio selects between keeping current time and
  // picking a new one. New time uses the shared booking-calendar (drop-in
  // slot picker - it fetches the new host's availability by hostUsername).
  timeChoice: 'keep' | 'new' = 'keep';
  newStart: Date | null = null;
  newEnd: Date | null = null;

  // Fetched on host change so the calendar can scope slots to the new host's
  // public booking username. getOrCreateForUser is idempotent + collision-safe,
  // so this always resolves to a valid record even for hosts that never set
  // themselves up. Cleared when host changes so a stale username never leaks.
  newHostBookingUsername: string = '';
  loadingNewHostSettings: boolean = false;
  // Force the calendar to remount + refetch when the new host or eventId
  // changes. booking-calendar watches this input in ngOnChanges.
  calendarRefreshTrigger: number = 0;

  // Live pre-check result from the backend when a host is selected. Drives
  // the green/amber status line (matched template, would-clone template,
  // time availability). Null while loading or before a host is picked.
  previewResult: any = null;
  previewLoading: boolean = false;

  // In-flight state for the Confirm action. Dialog stays open while the
  // reassign + notification calls run so the admin sees a spinner instead
  // of the dialog vanishing and everything happening invisibly.
  submitting: boolean = false;
  submitError: string = '';


  constructor(
    private _dialogRef: MatDialogRef<BookingReassignDialogComponent>,
    private _userSettingsService: YeehroCalendlyUserSettingsService,
    private _bookingService: YeehroCalendlyBookingService,
    private _availabilityService: YeehroCalendlyAvailabilityService,
    private _workflowService: YeehroCalendlyWorkflowService,
    private _authService: AuthService
  ) {}


  async ngOnInit() {
    if (!this.booking?._id) {
      // Nothing to reassign - close immediately rather than render a blank shell.
      this._dialogRef?.close(null);
      return;
    }
    await this.loadAvailableHosts();
  }


  // Pre-load the recruiter list from the availability service so the
  // dropdown only shows admins we can actually reassign to.
  private async loadAvailableHosts() {
    try {
      const res = await lastValueFrom(this._availabilityService.getHostsWithAvailability());
      const currentHostId = String(this.booking?.host?._id || this.booking?.host || '');
      // Filter out the current host so admin can't pick the same person.
      this.availableHosts = (res?.users || []).filter((u: any) => String(u?._id) !== currentHostId);
    } catch (err) {
      console.log('Error loading available hosts for reassign: ', err);
      this.availableHosts = [];
    }
    this.hostsLoaded = true;
  }


  get currentHostDisplayName(): string {
    const h = this.booking?.host;
    if (!h) return 'Unknown';
    const first = h?.preferred_first_name || h?.first_name || '';
    const last = h?.preferred_last_name || h?.last_name || '';
    const full = `${first} ${last}`.trim();
    return full || h?.email || 'Unknown';
  }


  get primaryInviteeDisplayName(): string {
    const inv = this.booking?.invitees?.[0];
    if (!inv) return '';
    return inv?.name || inv?.email || '';
  }


  get primaryInviteeEmail(): string {
    return this.booking?.invitees?.[0]?.email || '';
  }


  get isSameAsCurrentHost(): boolean {
    const currentId = this.booking?.host?._id?.toString() || this.booking?.host?.toString() || '';
    const pickedId = this.selectedHost?._id?.toString() || '';
    return !!pickedId && pickedId === currentId;
  }


  get bookingEventId(): string {
    return this.booking?.event?._id || this.booking?.event || 'none'; // Case 1502: template gone -> server uses the meeting's own rules
  }


  // Current booking's start passed to the calendar so it pre-highlights the
  // existing slot when admin flips to Pick-a-new-time.
  get currentBookingStart(): Date | null {
    if (!this.booking?.start) return null;
    return new Date(this.booking.start);
  }


  // Client-side check for whether the original booking time still leaves
  // enough notice under the event's min-notice rule. When it doesn't,
  // "Keep current time" gets disabled - admin must pick a new time.
  get keepCurrentDisabled(): boolean {
    if (!this.booking?.start) return true;
    const start = new Date(this.booking.start);
    if (isNaN(start.getTime())) return true;
    // Past bookings already hide the reassign button via showRescheduleCancel(),
    // so this really only fires for min-notice conflicts.
    const now = Date.now();
    const minNoticeMs = this._resolveMinNoticeMs();
    if (start.getTime() - now < minNoticeMs) return true;
    return false;
  }


  get keepCurrentDisabledReason(): string {
    if (!this.booking?.start) return 'Original time is unavailable.';
    const start = new Date(this.booking.start);
    const now = Date.now();
    if (start.getTime() <= now) return 'Original time has already passed.';
    return "Original time doesn't leave enough notice - pick a new time.";
  }


  // Convert event.minNotice + event.minNoticeUnit into milliseconds. Defaults
  // to zero when either is missing so we err toward "allowed".
  private _resolveMinNoticeMs(): number {
    const value = Number(this.booking?.event?.minNotice);
    const unit = String(this.booking?.event?.minNoticeUnit || '').toLowerCase();
    if (!Number.isFinite(value) || value <= 0) return 0;
    const perUnit: any = {
      'minute': 60_000, 'minutes': 60_000,
      'hour': 3_600_000, 'hours': 3_600_000,
      'day': 86_400_000, 'days': 86_400_000
    };
    return value * (perUnit[unit] || 3_600_000);
  }


  // Derived status class for the inline preview line under the host picker.
  // green = keeps the template, red = time conflict. Case 1502: reassign never switches or copies templates.
  get previewStatusClass(): string {
    if (!this.previewResult) return '';
    if (this.timeChoice === 'keep' && this.previewResult?.availableAtTime === false) return 'red';
    if (this.previewResult?.matchType === 'shared') return 'green';
    return '';
  }


  get previewStatusText(): string {
    if (!this.previewResult) return '';
    // Availability check message wins - if the host isn't free at the current
    // time (and admin hasn't opted into pick-new), that's the actionable info.
    if (this.timeChoice === 'keep' && this.previewResult?.availableAtTime === false) {
      const name = this.selectedHost?.first_name || 'This recruiter';
      return `${name} isn't available at the current meeting time - pick a new time below.`;
    }
    // Case 1502: the meeting keeps its template; only the host changes.
    if (this.previewResult?.matchType === 'shared' && this.previewResult?.matchedEvent?.name) {
      return `Keeps the "${this.previewResult.matchedEvent.name}" template. Only the host changes.`;
    }
    return '';
  }


  get canConfirm(): boolean {
    if (!this.selectedHost?._id) return false;
    if (this.isSameAsCurrentHost) return false;
    // If admin chose to change the time, a slot must actually be picked.
    if (this.timeChoice === 'new' && !this.newStart) return false;
    // If keeping current time but the new host isn't available then, block.
    if (this.timeChoice === 'keep' && this.previewResult?.availableAtTime === false) return false;
    // If keeping current time when min-notice makes it unreachable, block.
    if (this.timeChoice === 'keep' && this.keepCurrentDisabled) return false;
    return true;
  }


  // Whether the calendar should render. Waits until we have both the new host
  // AND their booking username - without either, the calendar can't fetch.
  get showCalendar(): boolean {
    return this.timeChoice === 'new' && !!this.selectedHost?._id && !!this.newHostBookingUsername;
  }


  async onHostChange(user: any) {
    this.selectedHost = user || null;
    // Any host change invalidates the previously-picked slot - the calendar
    // is per-host, so a slot on Recruiter A is meaningless for Recruiter B.
    this.newStart = null;
    this.newEnd = null;
    this.newHostBookingUsername = '';
    this.previewResult = null;

    if (!this.selectedHost?._id || this.isSameAsCurrentHost) return;
    // Fire both fetches in parallel so the calendar (if opened) and the
    // pre-check status show up together.
    await Promise.all([
      this.loadNewHostBookingUsername(),
      this.loadPreview()
    ]);
    // If admin is in "keep current" mode and the host is NOT available at
    // that time, auto-flip to "pick new" so the calendar comes up.
    if (this.timeChoice === 'keep' && this.previewResult?.availableAtTime === false) {
      this.timeChoice = 'new';
      this.bumpCalendarRefresh();
    }
  }


  private async loadNewHostBookingUsername() {
    if (!this.selectedHost?._id) return;
    this.loadingNewHostSettings = true;
    try {
      const settings = await lastValueFrom(this._userSettingsService.getOrCreateForUser(this.selectedHost._id));
      this.newHostBookingUsername = settings?.bookingUsername || '';
      this.bumpCalendarRefresh();
    } catch (err) {
      console.log('Error loading booking username for reassign host: ', err);
      this.newHostBookingUsername = '';
    }
    this.loadingNewHostSettings = false;
  }


  private async loadPreview() {
    if (!this.selectedHost?._id || !this.booking?._id) return;
    this.previewLoading = true;
    try {
      const startForCheck = this.timeChoice === 'new' && this.newStart ? this.newStart : this.booking?.start;
      this.previewResult = await lastValueFrom(
        this._bookingService.reassignPreview(this.booking._id, this.selectedHost._id, startForCheck)
      );
    } catch (err) {
      console.log('Error loading reassign preview: ', err);
      this.previewResult = null;
    }
    this.previewLoading = false;
  }


  onTimeChoiceChange(choice: 'keep' | 'new') {
    // Ignore attempts to flip to "keep" when it's currently disabled.
    if (choice === 'keep' && this.keepCurrentDisabled) return;
    this.timeChoice = choice;
    if (choice === 'keep') {
      // Reset any slot the admin had picked - going back to keep means the
      // original booking start/end wins.
      this.newStart = null;
      this.newEnd = null;
    } else {
      // Trigger a fresh render on switch so the calendar shows the newly
      // active host's availability, not stale data from a prior open.
      this.bumpCalendarRefresh();
    }
  }


  // Booking-calendar emits full slot detail (date, time, start/end Dates,
  // timezone). Capture the Date objects for the payload; the ISO conversion
  // happens when the reassign endpoint gets called.
  onSlotSelected(slot: { date: string; time: string; startTime: Date; endTime: Date; timezone: string }) {
    this.newStart = slot?.startTime || null;
    this.newEnd = slot?.endTime || null;
  }


  onSlotDeselected() {
    this.newStart = null;
    this.newEnd = null;
  }


  private bumpCalendarRefresh() {
    this.calendarRefreshTrigger = (this.calendarRefreshTrigger || 0) + 1;
  }


  async onConfirm() {
    if (!this.canConfirm || this.submitting) return;

    // Snapshot pre-reassign state for the notification payload BEFORE we
    // mutate anything - former host + former start/end drive the "was hosted
    // by X" and "former time" lines in the emails.
    const formerHostId = String(this.booking?.host?._id || this.booking?.host || '');
    const formerStart = this.booking?.start || null;
    const formerEnd = this.booking?.end || null;
    const timeChanged = this.timeChoice === 'new' && !!this.newStart;
    const notePayload = this.note?.trim() || '';

    this.submitting = true;
    this.submitError = '';

    // Step 1: the reassign endpoint (host swap + event match/clone + audit).
    let updated: any = null;
    try {
      updated = await lastValueFrom(this._bookingService.reassign(this.booking._id, {
        newHostId: this.selectedHost?._id,
        newStart: timeChanged ? this.newStart : null,
        newEnd: timeChanged ? this.newEnd : null,
        note: notePayload
      }));
    } catch (err) {
      console.log('Reassign save errored: ', err);
      this.submitError = 'Failed to reassign the meeting. Please try again.';
      this.submitting = false;
      return;
    }

    if (!updated?._id) {
      this.submitError = 'Reassign did not complete. Please try again.';
      this.submitting = false;
      return;
    }

    // Step 2: notification (calendar swap + 3 emails). Non-blocking on failure -
    // the reassign itself has already persisted; we still close the dialog and
    // let the parent flag any email-send warning via its toast.
    let notificationResult: any = null;
    try {
      notificationResult = await lastValueFrom(this._workflowService.processReassignNotification({
        bookingId: this.booking._id,
        formerHostId: formerHostId,
        performedBy: this.getCurrentUserDisplayName(),
        note: notePayload,
        timeChanged: timeChanged,
        formerStart: formerStart,
        formerEnd: formerEnd
      }));
    } catch (err) {
      console.log('Reassign notification errored: ', err);
    }

    this.submitting = false;
    this._dialogRef?.close({
      updated: updated,
      notificationResult: notificationResult,
      timeChanged: timeChanged
    });
  }


  onCancel() {
    // Don't allow cancel to close mid-write - the backend call is already
    // in flight and cancelling the dialog can't roll it back. Once submit
    // starts, Cancel becomes a no-op until the request resolves.
    if (this.submitting) return;
    this._dialogRef?.close(null);
  }


  // Display name for the current admin - used as `performedBy` in the
  // notification payload so the emails and audit entry read "by {name}".
  private getCurrentUserDisplayName(): string {
    const u = this._authService?.getCurrentlyLoggedInUser?.();
    if (!u) return '';
    const first = u?.preferred_first_name || u?.first_name || '';
    const last = u?.preferred_last_name || u?.last_name || '';
    return `${first} ${last}`.trim() || u?.email || '';
  }
}
